/**
 * Segmentos e caminhos absolutos das rotas do portal.
 * Valores devem permanecer alinhados a `app.routes.ts`.
 */

export const ROUTE_SEGMENTS = {
  home: '',
  login: 'login',
  portal: 'portal',
  companies: 'empresas',
  visitAgenda: 'agenda-visitas',
  field: 'campo',
  myRoute: 'minha-rota',
  fieldDashboard: 'painel',
  fieldSheet: 'validar-ficha',
  fieldRoutes: 'rotas',
  fieldAudit: 'auditoria',
  fieldHomologation: 'homologacao',
  landing: 'vitrine',
  landingPersonalization: 'personalizar-vitrine',
  digitalYearbook: 'anuario-digital',
  passaporte: 'passaporte',
  clientHomologation: 'homologar',
  fieldFinalSheet: 'ficha-final',
  fieldReviews: 'revisoes',
  fieldDiscoveries: 'descobertas',
  fieldSelfValidation: 'autovalidacoes',
  fieldTeams: 'equipes',
  fieldSupportQueue: 'atendimentos',
  clientBriefing: 'briefing-cliente',
  vipEditorial: 'vip-editorial',
  admin: 'admin',
  adminDataOps: 'dados',
  platformSettings: 'configuracoes',
  adminLodging: 'hospedagem',
  adminFleet: 'frota',
  cities: 'cidades',
  guide: 'guia',
  conference: 'conferencia',
  contract: 'contrato',
  privacy: 'privacidade',
  empresa: 'empresa',
  selfValidation: 'autovalidacao',
  yearbook: 'anuario',
  tutorials: 'tutoriais',
  faq: 'faq',
  validate: 'validar',
  audit: 'auditoria',
  call: 'ligacao',
  visit: 'visita',
  physical: 'midia-fisica',
  homologation: 'homologacao',
  commercialFormalization: 'formalizacao-comercial',
  acceptance: 'aceite',
  /** URL pública de cadastro (QR). Chave interna: intake. */
  intake: 'cadastro',
  clients: 'clientes',
  selfFill: 'auto-preenchimento',
  myCompany: 'minha-empresa',
  referrals: 'indique-e-ganhe',
  reservations: 'reservas',
  menu: 'cardapio',
  users: 'usuarios',
  usersNew: 'novo',
  usersEdit: 'editar',
  underConstruction: 'em-construcao',
  underMaintenance: 'em-manutencao'
} as const;

export type AuditRouteSegment =
  | typeof ROUTE_SEGMENTS.call
  | typeof ROUTE_SEGMENTS.visit
  | typeof ROUTE_SEGMENTS.physical
  | typeof ROUTE_SEGMENTS.homologation;

/** Caminhos absolutos para `router.navigate` / `routerLink`. */
export const APP_PATHS = {
  home: '/',
  login: `/${ROUTE_SEGMENTS.login}`,
  portal: `/${ROUTE_SEGMENTS.portal}`,
  companies: `/${ROUTE_SEGMENTS.companies}`,
  visitAgenda: `/${ROUTE_SEGMENTS.visitAgenda}`,
  myRoute: `/${ROUTE_SEGMENTS.field}/${ROUTE_SEGMENTS.myRoute}`,
  fieldDashboard: `/${ROUTE_SEGMENTS.field}/${ROUTE_SEGMENTS.fieldDashboard}`,
  fieldRoutes: `/${ROUTE_SEGMENTS.field}/${ROUTE_SEGMENTS.fieldRoutes}`,
  /** @deprecated Redireciona para /clientes */
  fieldAudit: `/${ROUTE_SEGMENTS.clients}`,
  /** @deprecated Redireciona para /clientes */
  fieldHomologation: `/${ROUTE_SEGMENTS.clients}`,
  fieldReviews: `/${ROUTE_SEGMENTS.field}/${ROUTE_SEGMENTS.fieldReviews}`,
  fieldDiscoveries: `/${ROUTE_SEGMENTS.field}/${ROUTE_SEGMENTS.fieldDiscoveries}`,
  fieldSelfValidation: `/${ROUTE_SEGMENTS.field}/${ROUTE_SEGMENTS.fieldSelfValidation}`,
  fieldTeams: `/${ROUTE_SEGMENTS.field}/${ROUTE_SEGMENTS.fieldTeams}`,
  fieldSupportQueue: `/${ROUTE_SEGMENTS.field}/${ROUTE_SEGMENTS.fieldSupportQueue}`,
  vipEditorial: `/${ROUTE_SEGMENTS.field}/${ROUTE_SEGMENTS.vipEditorial}`,
  adminDataOps: `/${ROUTE_SEGMENTS.admin}/${ROUTE_SEGMENTS.adminDataOps}`,
  platformSettings: `/${ROUTE_SEGMENTS.admin}/${ROUTE_SEGMENTS.platformSettings}`,
  adminLodging: `/${ROUTE_SEGMENTS.admin}/${ROUTE_SEGMENTS.adminLodging}`,
  adminFleet: `/${ROUTE_SEGMENTS.admin}/${ROUTE_SEGMENTS.adminFleet}`,
  cities: `/${ROUTE_SEGMENTS.cities}`,
  guide: `/${ROUTE_SEGMENTS.guide}`,
  yearbook: `/${ROUTE_SEGMENTS.yearbook}`,
  digitalYearbook: `/${ROUTE_SEGMENTS.digitalYearbook}`,
  passaporte: `/${ROUTE_SEGMENTS.passaporte}`,
  passaporteEnter: `/${ROUTE_SEGMENTS.passaporte}/entrar`,
  passaporteRegister: `/${ROUTE_SEGMENTS.passaporte}/cadastro`,
  passaporteAccount: `/${ROUTE_SEGMENTS.passaporte}/conta`,
  tutorials: `/${ROUTE_SEGMENTS.tutorials}`,
  faq: `/${ROUTE_SEGMENTS.faq}`,
  intake: `/${ROUTE_SEGMENTS.intake}`,
  acceptance: `/${ROUTE_SEGMENTS.acceptance}`,
  clients: `/${ROUTE_SEGMENTS.clients}`,
  myCompany: `/${ROUTE_SEGMENTS.myCompany}`,
  myCompanyReferrals: `/${ROUTE_SEGMENTS.myCompany}/${ROUTE_SEGMENTS.referrals}`,
  myCompanyReservations: `/${ROUTE_SEGMENTS.myCompany}/${ROUTE_SEGMENTS.reservations}`,
  myCompanyMenu: `/${ROUTE_SEGMENTS.myCompany}/${ROUTE_SEGMENTS.menu}`,
  users: `/${ROUTE_SEGMENTS.users}`,
  usersNew: `/${ROUTE_SEGMENTS.users}/${ROUTE_SEGMENTS.usersNew}`,
  underConstruction: `/${ROUTE_SEGMENTS.underConstruction}`,
  underMaintenance: `/${ROUTE_SEGMENTS.underMaintenance}`
} as const;

/** Rotas públicas de cadastro (sem autenticação). */
export const PUBLIC_CADASTRO_ROUTES = [
  ROUTE_SEGMENTS.intake,
  `${ROUTE_SEGMENTS.intake}/:cidade`,
  `${ROUTE_SEGMENTS.intake}/:cidade/${ROUTE_SEGMENTS.privacy}`,
  ROUTE_SEGMENTS.guide,
  `${ROUTE_SEGMENTS.guide}/:cidade`,
  `${ROUTE_SEGMENTS.conference}/:token`,
  `${ROUTE_SEGMENTS.contract}/:token`,
  `${ROUTE_SEGMENTS.acceptance}/:token`,
  `${ROUTE_SEGMENTS.empresa}/${ROUTE_SEGMENTS.selfValidation}/:token`
] as const;

/**
 * Aviso de privacidade Atlas Passaporte (documento estático, sem dados do cliente).
 * Caminho do portal atual. O endereço público futuro é https://atlasneural.ai/privacidade-passaporte.
 */
export const ATLAS_PASSAPORTE_PRIVACY_NOTICE_URL = '/privacidade-passaporte';

export function fieldSheetPath(enrichmentId: string): string {
  return `/${ROUTE_SEGMENTS.field}/${ROUTE_SEGMENTS.fieldSheet}/${enrichmentId}`;
}

export function clientBriefingPath(enrichmentId: string): string {
  return `/${ROUTE_SEGMENTS.field}/${ROUTE_SEGMENTS.clientBriefing}/${enrichmentId}`;
}

/** Cadastro in loco piloto — briefings de I.A. sem limite (espelha regra da API). */
export const UNLIMITED_CLIENT_BRIEFING_ENRICHMENT_IDS = new Set<string>([
  '968f4f61-7acb-4aa8-92df-8c73f21b8261'
]);

export function hasUnlimitedClientBriefing(enrichmentId: string): boolean {
  return UNLIMITED_CLIENT_BRIEFING_ENRICHMENT_IDS.has(enrichmentId.trim().toLowerCase());
}

export function landingPath(enrichmentId: string): string {
  return `/${ROUTE_SEGMENTS.landing}/${enrichmentId}`;
}

export function landingPersonalizationPath(enrichmentId: string): string {
  return `/${ROUTE_SEGMENTS.landingPersonalization}/${enrichmentId}`;
}

/** Personalização autenticada (gestão · Clientes). */
export function clientLandingPersonalizationPath(enrichmentId: string): string {
  return `/${ROUTE_SEGMENTS.clients}/${enrichmentId}/personalizar-vitrine`;
}

export function digitalYearbookPath(): string {
  return `/${ROUTE_SEGMENTS.digitalYearbook}`;
}

export function passaportePath(): string {
  return `/${ROUTE_SEGMENTS.passaporte}`;
}

export function passaporteCompanyPath(enrichmentId: string): string {
  return `/${ROUTE_SEGMENTS.passaporte}/${enrichmentId}`;
}

export function empresaPublicPath(citySlug: string, slug: string): string {
  return `/${ROUTE_SEGMENTS.empresa}/${citySlug}/${slug}`;
}

export function clientHomologationPath(enrichmentId: string): string {
  return `/${ROUTE_SEGMENTS.clientHomologation}/${enrichmentId}`;
}

export function fieldFinalSheetPath(enrichmentId: string): string {
  return `/${ROUTE_SEGMENTS.field}/${ROUTE_SEGMENTS.fieldFinalSheet}/${enrichmentId}`;
}

export function selfValidationPath(token: string): string {
  return `/${ROUTE_SEGMENTS.empresa}/${ROUTE_SEGMENTS.selfValidation}/${token}`;
}

export function companyValidationPath(enrichmentId: string): string {
  return `/${ROUTE_SEGMENTS.companies}/${enrichmentId}/${ROUTE_SEGMENTS.validate}`;
}

export function companyAuditPath(
  enrichmentId: string,
  segment: AuditRouteSegment
): string {
  return `/${ROUTE_SEGMENTS.companies}/${enrichmentId}/${ROUTE_SEGMENTS.audit}/${segment}`;
}

export function companyHomologationPath(enrichmentId: string): string {
  return companyAuditPath(enrichmentId, ROUTE_SEGMENTS.homologation);
}

export function companyCommercialFormalizationPath(enrichmentId: string): string {
  return `/${ROUTE_SEGMENTS.companies}/${enrichmentId}/${ROUTE_SEGMENTS.commercialFormalization}`;
}

export function acceptancePath(token: string): string {
  return `/${ROUTE_SEGMENTS.acceptance}/${token}`;
}

export function intakePath(): string {
  return APP_PATHS.intake;
}

export function guidePath(): string {
  return APP_PATHS.guide;
}

export function guideCityPath(citySlug: string): string {
  return `/${ROUTE_SEGMENTS.guide}/${citySlug}`;
}

export function cityIntakePath(citySlug: string): string {
  return `/${ROUTE_SEGMENTS.intake}/${citySlug}`;
}

export function cityIntakePrivacyPath(citySlug: string): string {
  return `/${ROUTE_SEGMENTS.intake}/${citySlug}/${ROUTE_SEGMENTS.privacy}`;
}

export function conferencePath(token: string): string {
  return `/${ROUTE_SEGMENTS.conference}/${token}`;
}

export function contractPath(token: string): string {
  return `/${ROUTE_SEGMENTS.contract}/${token}`;
}

export function userDetailPath(userId: string): string {
  return `/${ROUTE_SEGMENTS.users}/${userId}`;
}

export function userEditPath(userId: string): string {
  return `/${ROUTE_SEGMENTS.users}/${userId}/${ROUTE_SEGMENTS.usersEdit}`;
}
